import type { FilingFormState } from './filing-form-state.service';
import { SHEET_KEYS } from '../../models/sheet-keys';
import { WARNING_CONTROLS_BY_STATE_KEY } from './warning-schema-registry';

/**
 * Which sheets CAN raise a `hasSuggestedChanges` verdict.
 *
 * ## Why capability, not current state
 *
 * The filing form's "(*) Optional: Suggestions to refine data" legend explains
 * what the red `*` beside a sheet name MEANS. It is a caption for a capability,
 * so it renders on any sheet that could produce one — not only on a sheet that
 * happens to have one right now. Gating it on the live flag would make the
 * caption blink in and out as the user edits, which is the opposite of a legend.
 *
 * ## The two mechanisms that set the flag
 *
 * 1. **Schema warnings** — a sheet listed in `WARNING_CONTROLS_BY_STATE_KEY`
 *    whose controls declare `warnings` in their form JSON. Roles 2, 3, 4, 5, 7
 *    (roles 0 and 1 dropped out on 2026-08-12 when their two financial-year
 *    date advisories were withdrawn from the schemas). Derived below, never
 *    restated, so this half cannot drift.
 * 2. **Calc-diff** — a monetary sheet whose pure validator compares recomputed
 *    totals against the immutable N8N extraction via
 *    `hasCalculatedFieldMismatch`. Roles 8, 9, 12, 13, 15-410000, 18, 19. This
 *    half is a code path inside seven validators, not a data structure, so it
 *    cannot derive itself and is listed explicitly.
 *
 * The remaining 14 sheets never raise the flag — 12 hard-code
 * `hasSuggestedChanges: false`, and roles 0/1 now compute it from a schema that
 * declares no warnings, so it is structurally always false. Six of the 12
 * (roles 10, 11, 14, 15-320100, 16, 17) carry a `Developer Note (TODO)` stub
 * saying so — role 14's puts it plainly: "this sheet's dropdown `*` can never
 * light". Releasing one of those as a live monetary sheet means adding its key
 * to {@link CALC_DIFF_SHEETS} as well as to `PURE_VERDICT_SHEETS`.
 *
 * Note this describes FRONTEND capability. `applyApiSheetStatuses` in the
 * filing-form container will set the flag for any sheet the backend returns
 * `MbrsStatusEnum.SuccessWithWarnings` for. By design that should not happen
 * for a non-capable sheet; if it is ever observed, the legend gate is where to
 * revisit it (developer ruling, 2026-08-11 — accepted, unguarded).
 */
export const CALC_DIFF_SHEETS: readonly (keyof FilingFormState)[] = [
  SHEET_KEYS.statementOfFinancialPosition,
  SHEET_KEYS.subClassificationOfAssetsLiabilitiesEquity,
  SHEET_KEYS.statementOfProfitOrLoss,
  SHEET_KEYS.analysisOfProfitOrLoss,
  SHEET_KEYS.statementOfComprehensiveIncome,
  SHEET_KEYS.statementOfCashFlowsIndirectMethod,
  SHEET_KEYS.statementOfChangesInEquity,
];

export const SUGGESTION_CAPABLE_SHEETS: ReadonlySet<string> = new Set<string>([
  ...Object.keys(WARNING_CONTROLS_BY_STATE_KEY),
  ...CALC_DIFF_SHEETS,
]);
